/**
 * Reports page -- Configurable reporting suite for officers and admins.
 * Sprint 12, SDD 8.1-8.4, FR-REPORT-01 through FR-REPORT-07
 *
 * Features:
 * - Report type selector (LOSAP Hours, Operational Detail, Attendance)
 * - Date range controls (month, calendar year, fiscal year Jul-Jun, custom)
 * - Filters: battalion, position, apparatus, member search
 * - Inline data table display with responsive cards on mobile
 * - CSV/PDF export
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isAdmin as isAdminRole } from '../lib/auth';
import { apiFetch } from '../lib/api';
import AppLayout from '../components/layout/AppLayout';
import { Download, FileText, Search, Calendar, Filter, ChevronDown, ChevronUp } from 'lucide-react';

// -- Types --

type ReportType = 'hours' | 'operational' | 'attendance';
type DateMode = 'month' | 'calendar_year' | 'fiscal_year' | 'custom';

interface DateRange {
  start: string;
  end: string;
  label: string;
}

interface HoursRow {
  member_id: number;
  display_name: string;
  email: string;
  battalion_name: string | null;
  operational_hours: number;
  administrative_hours: number;
  training_hours: number;
  public_event_hours: number;
  total_hours: number;
}

interface OperationalRow {
  member_name: string;
  member_battalion: string | null;
  clock_in: string;
  clock_out: string;
  duration_minutes: number;
  position: string | null;
  apparatus: string | null;
  shift_type: string;
  source: string;
  duty_battalion: string | null;
}

interface AttendanceRow {
  date: string;
  battalion_name: string;
  shift_start: string;
  shift_end: string;
  member_name: string;
  email: string;
  attendance_status: string;
  clock_in: string | null;
  clock_out: string | null;
  duration_minutes: number | null;
}

interface HoursResponse {
  date_range: DateRange;
  data: HoursRow[];
  total_members: number;
}
interface OperationalResponse {
  date_range: DateRange;
  data: OperationalRow[];
  total_entries: number;
}
interface AttendanceResponse {
  date_range: DateRange;
  data: AttendanceRow[];
  summary: { total_records: number; present: number; absent: number; attendance_rate: number };
  warnings: string[];
}

// -- Helpers --

function getReportLabel(type: ReportType): string {
  switch (type) {
    case 'hours':
      return 'LOSAP Hours';
    case 'operational':
      return 'Operational Detail';
    case 'attendance':
      return 'Attendance';
  }
}

function getDateModeLabel(mode: DateMode): string {
  switch (mode) {
    case 'month':
      return 'Month';
    case 'calendar_year':
      return 'Calendar Year';
    case 'fiscal_year':
      return 'Fiscal Year (Jul-Jun)';
    case 'custom':
      return 'Custom Range';
  }
}

function toDateInputValue(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '--';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDate(iso: string | null): string {
  if (!iso) return '--';
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// Fiscal year: July 1 to June 30 (Virginia/PWC standard)
function getFiscalYearRange(fy: number): { start: string; end: string } {
  return { start: `${fy - 1}-07-01`, end: `${fy}-06-30` };
}

function getCurrentFiscalYear(): number {
  const now = new Date();
  return now.getMonth() >= 6 ? now.getFullYear() + 1 : now.getFullYear();
}

// -- Component --

export default function Reports() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [reportType, setReportType] = useState<ReportType>('hours');
  const [dateMode, setDateMode] = useState<DateMode>('month');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [fiscalYear, setFiscalYear] = useState(getCurrentFiscalYear());
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return toDateInputValue(d);
  });
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(new Date()));
  const [battalionId, setBattalionId] = useState<string>('');
  const [memberSearch, setMemberSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoursData, setHoursData] = useState<HoursResponse | null>(null);
  const [operationalData, setOperationalData] = useState<OperationalResponse | null>(null);
  const [attendanceData, setAttendanceData] = useState<AttendanceResponse | null>(null);

  const isAdmin = isAdminRole(user);

  const buildParams = useCallback(() => {
    const params = new URLSearchParams();
    params.set('mode', dateMode);
    switch (dateMode) {
      case 'month':
        params.set('year', String(year));
        params.set('month', String(month));
        break;
      case 'calendar_year':
        params.set('year', String(year));
        break;
      case 'fiscal_year': {
        const range = getFiscalYearRange(fiscalYear);
        params.set('mode', 'custom');
        params.set('start', range.start);
        params.set('end', range.end);
        break;
      }
      case 'custom':
        params.set('start', customStart);
        params.set('end', customEnd);
        break;
    }
    if (isAdmin && battalionId) params.set('battalion_id', battalionId);
    return params;
  }, [dateMode, year, month, fiscalYear, customStart, customEnd, isAdmin, battalionId]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHoursData(null);
    setOperationalData(null);
    setAttendanceData(null);
    try {
      const params = buildParams();
      const endpoint = `/api/reports/${reportType}?${params.toString()}`;
      switch (reportType) {
        case 'hours': {
          setHoursData(await apiFetch<HoursResponse>(endpoint));
          break;
        }
        case 'operational': {
          setOperationalData(await apiFetch<OperationalResponse>(endpoint));
          break;
        }
        case 'attendance': {
          setAttendanceData(await apiFetch<AttendanceResponse>(endpoint));
          break;
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [reportType, buildParams]);

  const handleDownloadCSV = useCallback(() => {
    const params = buildParams();
    params.set('format', 'csv');
    window.open(`/api/reports/${reportType}?${params.toString()}`, '_blank');
  }, [reportType, buildParams]);

  const handleDownloadPDF = useCallback(() => {
    const params = buildParams();
    params.set('type', reportType);
    window.open(`/api/reports/pdf?${params.toString()}`, '_blank');
  }, [reportType, buildParams]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  if (user && user.role === 'member') {
    navigate('/');
    return null;
  }

  const filteredHoursData = hoursData?.data.filter(
    (r) => !memberSearch || r.display_name.toLowerCase().includes(memberSearch.toLowerCase()),
  );
  const filteredOperationalData = operationalData?.data.filter(
    (r) => !memberSearch || r.member_name.toLowerCase().includes(memberSearch.toLowerCase()),
  );

  return (
    <AppLayout title="Reports">
      <div className="w-full pt-4">
        <h1 className="text-xl font-bold text-content-primary mb-6">Reports</h1>

        {/* Report type selector */}
        <div className="grid grid-cols-3 gap-2 mb-6">
          {(['hours', 'operational', 'attendance'] as ReportType[]).map((t) => {
            const active = reportType === t;
            return (
              <button
                key={t}
                type="button"
                onClick={() => setReportType(t)}
                className={`px-3 py-3 rounded-xl text-sm font-medium text-center transition-all duration-150 cursor-pointer border
                  ${
                    active
                      ? 'bg-action-primary/15 border-action-primary text-action-primary ring-1 ring-action-primary/30'
                      : 'bg-surface-raised border-edge-default text-content-secondary hover:border-edge-strong'
                  }`}
              >
                {getReportLabel(t)}
              </button>
            );
          })}
        </div>

        {/* Date range controls */}
        <div className="bg-surface-raised border border-edge-default rounded-2xl p-4 mb-4">
          <div className="text-xs font-semibold text-content-secondary uppercase tracking-wider mb-3">
            <Calendar className="h-3.5 w-3.5 inline mr-1.5" />
            Date Range
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {(['month', 'calendar_year', 'fiscal_year', 'custom'] as DateMode[]).map((m) => {
              const active = dateMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => setDateMode(m)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-150 cursor-pointer whitespace-nowrap
                    ${
                      active
                        ? 'border-action-primary text-action-primary bg-action-primary/10 font-bold'
                        : 'border-edge-default text-content-secondary bg-surface-sunken hover:border-edge-strong'
                    }`}
                >
                  {getDateModeLabel(m)}
                </button>
              );
            })}
          </div>
          <div className="space-y-2">
            {dateMode === 'month' && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label htmlFor="report-year" className="text-xs text-content-tertiary block mb-1">
                    Year
                  </label>
                  <input
                    id="report-year"
                    type="number"
                    value={year}
                    onChange={(e) => setYear(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="report-month" className="text-xs text-content-tertiary block mb-1">
                    Month
                  </label>
                  <select
                    id="report-month"
                    value={month}
                    onChange={(e) => setMonth(parseInt(e.target.value, 10))}
                    className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <option key={m} value={m}>
                        {new Date(2026, m - 1, 1).toLocaleDateString('en-US', { month: 'long' })}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            {dateMode === 'calendar_year' && (
              <div className="max-w-[200px]">
                <label htmlFor="report-cy" className="text-xs text-content-tertiary block mb-1">
                  Year
                </label>
                <input
                  id="report-cy"
                  type="number"
                  value={year}
                  onChange={(e) => setYear(parseInt(e.target.value, 10))}
                  className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                />
              </div>
            )}
            {dateMode === 'fiscal_year' && (
              <div>
                <label htmlFor="report-fy" className="text-xs text-content-tertiary block mb-1">
                  Fiscal Year (Jul {fiscalYear - 1} - Jun {fiscalYear})
                </label>
                <div className="flex gap-2 items-center">
                  <input
                    id="report-fy"
                    type="number"
                    value={fiscalYear}
                    onChange={(e) => setFiscalYear(parseInt(e.target.value, 10))}
                    className="w-[120px] px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  />
                  <span className="text-xs text-content-tertiary">
                    Jul 1, {fiscalYear - 1} through Jun 30, {fiscalYear}
                  </span>
                </div>
              </div>
            )}
            {dateMode === 'custom' && (
              <div className="flex gap-3">
                <div className="flex-1">
                  <label htmlFor="report-start" className="text-xs text-content-tertiary block mb-1">
                    Start
                  </label>
                  <input
                    id="report-start"
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  />
                </div>
                <div className="flex-1">
                  <label htmlFor="report-end" className="text-xs text-content-tertiary block mb-1">
                    End
                  </label>
                  <input
                    id="report-end"
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional filters */}
        <div className="mb-4">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-sm text-content-secondary hover:text-content-primary transition-colors duration-150 cursor-pointer mb-2"
          >
            <Filter className="h-4 w-4" />
            <span>Additional Filters</span>
            {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showFilters && (
            <div className="bg-surface-raised border border-edge-default rounded-xl p-4 space-y-3">
              {isAdmin && (
                <div>
                  <label className="text-xs text-content-tertiary block mb-1">Battalion</label>
                  <select
                    value={battalionId}
                    onChange={(e) => setBattalionId(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary outline-none focus:ring-2 focus:ring-edge-focus"
                  >
                    <option value="">All Battalions</option>
                    {[1, 2, 3, 4, 5].map((num) => (
                      <option key={num} value={String(num)}>
                        Battalion {num}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs text-content-tertiary block mb-1">Search Members</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-tertiary" />
                  <input
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    placeholder="Type a name..."
                    className="w-full pl-9 pr-3 py-2 text-sm bg-surface-sunken border border-edge-default rounded-lg text-content-primary placeholder:text-content-tertiary outline-none focus:ring-2 focus:ring-edge-focus"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Export buttons */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={handleDownloadCSV}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-action-primary text-content-on-primary rounded-lg cursor-pointer hover:bg-action-primary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4" /> CSV
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-content-primary bg-surface-raised border border-edge-default rounded-lg cursor-pointer hover:bg-action-secondary-hover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <FileText className="h-4 w-4" /> PDF
          </button>
        </div>

        {/* Status line */}
        <div className="mb-3 text-sm text-content-tertiary">
          {loading && 'Loading report...'}
          {error && <span className="text-danger-400">{error}</span>}
          {!loading && !error && hoursData && `${filteredHoursData?.length ?? 0} of ${hoursData.total_members} members`}
          {!loading &&
            !error &&
            operationalData &&
            `${filteredOperationalData?.length ?? 0} of ${operationalData.total_entries} entries`}
          {!loading && !error && attendanceData && (
            <span>
              {attendanceData.summary.total_records} records &middot; {attendanceData.summary.present} present &middot;{' '}
              {attendanceData.summary.absent} absent &middot; {attendanceData.summary.attendance_rate}% rate
            </span>
          )}
        </div>

        {attendanceData?.warnings?.map((w, i) => (
          <div
            key={i}
            className="px-3 py-2 mb-2 text-sm text-warning-500 bg-warning-500/10 border border-warning-500/25 rounded-lg"
          >
            {w}
          </div>
        ))}

        {/* Report data */}
        <div className="overflow-x-auto">
          {filteredHoursData && filteredHoursData.length > 0 && (
            <>
              <table className="hidden sm:table w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Member</Th>
                    <Th>Battalion</Th>
                    <Th>Operational</Th>
                    <Th>Admin</Th>
                    <Th>Training</Th>
                    <Th>Public Event</Th>
                    <Th>Total</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHoursData.map((row) => (
                    <tr key={row.member_id} className="hover:bg-surface-sunken/50">
                      <Td>{row.display_name}</Td>
                      <Td>{row.battalion_name ?? '--'}</Td>
                      <Td>{row.operational_hours}</Td>
                      <Td>{row.administrative_hours}</Td>
                      <Td>{row.training_hours}</Td>
                      <Td>{row.public_event_hours}</Td>
                      <Td>
                        <span className="font-semibold text-action-primary">{row.total_hours}</span>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden flex flex-col gap-2">
                {filteredHoursData.map((row) => (
                  <div key={row.member_id} className="bg-surface-raised border border-edge-muted rounded-xl p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-sm text-content-primary">{row.display_name}</div>
                      <span className="text-sm font-bold text-action-primary">{row.total_hours}h</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs text-content-secondary">
                      <span>Ops: {row.operational_hours}h</span>
                      <span>Admin: {row.administrative_hours}h</span>
                      <span>Training: {row.training_hours}h</span>
                      <span>Pub Event: {row.public_event_hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {filteredOperationalData && filteredOperationalData.length > 0 && (
            <>
              <table className="hidden sm:table w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Member</Th>
                    <Th>Battalion</Th>
                    <Th>Clock In</Th>
                    <Th>Clock Out</Th>
                    <Th>Duration</Th>
                    <Th>Position</Th>
                    <Th>Apparatus</Th>
                    <Th>Shift</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOperationalData.map((row, i) => (
                    <tr key={i} className="hover:bg-surface-sunken/50">
                      <Td>{row.member_name}</Td>
                      <Td>{row.member_battalion ?? '--'}</Td>
                      <Td>{formatDateTime(row.clock_in)}</Td>
                      <Td>{formatDateTime(row.clock_out)}</Td>
                      <Td>{row.duration_minutes ? `${Math.round((row.duration_minutes / 60) * 10) / 10}h` : '--'}</Td>
                      <Td>{row.position ?? '--'}</Td>
                      <Td>{row.apparatus ?? '--'}</Td>
                      <Td>{row.shift_type}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden flex flex-col gap-2">
                {filteredOperationalData.map((row, i) => (
                  <div key={i} className="bg-surface-raised border border-edge-muted rounded-xl p-3">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm text-content-primary">{row.member_name}</span>
                      <span className="text-xs font-semibold text-action-primary">
                        {row.duration_minutes ? `${Math.round((row.duration_minutes / 60) * 10) / 10}h` : '--'}
                      </span>
                    </div>
                    <div className="text-xs text-content-secondary">
                      {formatDateTime(row.clock_in)} - {formatDateTime(row.clock_out)}
                    </div>
                    <div className="text-xs text-content-tertiary mt-0.5">
                      {[row.position, row.apparatus, row.shift_type].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {attendanceData && attendanceData.data.length > 0 && (
            <>
              <table className="hidden sm:table w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Date</Th>
                    <Th>Battalion</Th>
                    <Th>Member</Th>
                    <Th>Status</Th>
                    <Th>Clock In</Th>
                    <Th>Clock Out</Th>
                    <Th>Duration</Th>
                  </tr>
                </thead>
                <tbody>
                  {attendanceData.data.map((row, i) => (
                    <tr key={i} className="hover:bg-surface-sunken/50">
                      <Td>{formatDate(row.date)}</Td>
                      <Td>{row.battalion_name}</Td>
                      <Td>{row.member_name}</Td>
                      <Td>
                        <span
                          className={`font-semibold ${row.attendance_status === 'Present' ? 'text-success-400' : 'text-danger-400'}`}
                        >
                          {row.attendance_status}
                        </span>
                      </Td>
                      <Td>{formatDateTime(row.clock_in)}</Td>
                      <Td>{formatDateTime(row.clock_out)}</Td>
                      <Td>{row.duration_minutes ? `${Math.round((row.duration_minutes / 60) * 10) / 10}h` : '--'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="sm:hidden flex flex-col gap-2">
                {attendanceData.data.map((row, i) => (
                  <div key={i} className="bg-surface-raised border border-edge-muted rounded-xl p-3">
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-sm text-content-primary">{row.member_name}</span>
                      <span
                        className={`text-xs font-semibold ${row.attendance_status === 'Present' ? 'text-success-400' : 'text-danger-400'}`}
                      >
                        {row.attendance_status}
                      </span>
                    </div>
                    <div className="text-xs text-content-secondary">
                      {formatDate(row.date)} &middot; {row.battalion_name}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading &&
            !error &&
            ((hoursData && hoursData.data.length === 0) ||
              (operationalData && operationalData.data.length === 0) ||
              (attendanceData && attendanceData.data.length === 0)) && (
              <div className="text-center py-10 text-content-tertiary text-sm">No data found for this period.</div>
            )}
        </div>
      </div>
    </AppLayout>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-3 py-2.5 text-xs font-semibold text-content-secondary uppercase tracking-wider border-b border-edge-default whitespace-nowrap">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-3 py-2.5 text-sm text-content-primary border-b border-edge-muted">{children}</td>;
}
